import { Plus } from 'lucide-react';
import { TLBaseShape, BaseBoxShapeUtil, HTMLContainer, RecordProps, T, stopEventPropagation, useDialogs } from 'tldraw';
import { v4 as uuidv4 } from 'uuid';

import { addNode } from '../../timelineDrawingHelpers';
import { NodeData } from '../../timelineSharedUtils';
import { NodeCreationModal } from '../Modals/NodeCreationModal';

type IAddInBetweenNodeButton = TLBaseShape<
  'add-in-between-node-button',
  {
    w: number;
    h: number;
    index: number;
  }
>;

export class AddInBetweenNodeButton extends BaseBoxShapeUtil<IAddInBetweenNodeButton> {
  static override type = 'add-in-between-node-button' as const;
  static override props: RecordProps<IAddInBetweenNodeButton> = {
    w: T.number,
    h: T.number,
    index: T.number,
  };

  getDefaultProps(): IAddInBetweenNodeButton['props'] {
    return {
      w: 100,
      h: 100,
      index: 0,
    };
  }

  component(shape: IAddInBetweenNodeButton) {
    const { addDialog } = useDialogs();

    const handleAddNewNode = async () => {
      this.editor.setCursor({ type: 'grabbing' });

      const insertIndex = shape.props.index;

      // Store node creation data for the dialog
      const nodeData = {
        insertIndex,
      };

      // Open TLDraw dialog
      addDialog({
        component: ({ onClose }) => (
          <NodeCreationModal onClose={onClose} onSave={(formData) => handleModalSave(formData, nodeData, onClose)} />
        ),
        onClose() {
          // Dialog closed without saving - cursor already reset
        },
      });

      this.editor.setCursor({ type: 'default' });
    };

    const handleModalSave = (
      nodeData: { dateText: string; description: string; documentNames: string; docId: string },
      pendingData: { insertIndex: number },
      onClose: () => void,
    ) => {
      const { insertIndex } = pendingData;
      const newNodeId = uuidv4();

      const newNodeData: NodeData = {
        nodeId: newNodeId,
        dateText: nodeData.dateText,
        date: '',
        description: nodeData.description,
        documentNames: nodeData.documentNames,
        docId: nodeData.docId,
      };

      // Use the new addNode function to insert at specific position
      addNode(this.editor, newNodeData, insertIndex);

      // Close the dialog
      onClose();
    };

    return (
      <HTMLContainer
        id={shape.id}
        className="group flex h-full w-full cursor-pointer items-center justify-center"
        style={{ pointerEvents: 'all' }}
      >
        <div className="rounded-full p-2 transition-colors duration-300 group-hover:bg-gray-50">
          <button
            onPointerDown={stopEventPropagation}
            onClick={handleAddNewNode}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-transparent transition-colors duration-300 group-hover:bg-green-500"
          >
            <Plus size={24} className="text-transparent transition-colors group-hover:text-white" />
          </button>
        </div>
      </HTMLContainer>
    );
  }

  indicator(shape: IAddInBetweenNodeButton) {
    return <rect width={shape.props.w} height={shape.props.h} />;
  }
}
